import { CommonModule, DatePipe } from '@angular/common';
import { Component, ChangeDetectorRef, Input, OnDestroy, TemplateRef, ViewChild } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { DialogComponent } from 'src/app/@core/components/ui/dialog/dialog.component';
import { DrawerModule } from 'primeng/drawer';
import { SkeletonModule } from 'primeng/skeleton';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmationService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { forkJoin, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { interval } from 'rxjs';
import { saveAs } from 'file-saver';
import { ButtonComponent } from '../../../../../@core/components/ui/button/button.component';
import { DeleteConfirmationComponent } from '../../../../../@core/components/ui/delete-confirmation/delete-confirmation.component';
import { ToastService } from '../../../../../@core/services/toast.service';
import { DataTableComponent } from '../../../../../@shared/components/data-table/data-table.component';
import { TableColumn, TableConfig } from '../../../../../@shared/models/table.models';
import { SearchBarComponent } from '../../../../../@shared/components/search-bar/search-bar.component';
import { UserAvatarPipe } from '../../../../../@shared/pipes/user-avatar-pipe';
import { PopoverComponent } from '../../../../../@core/components/ui/popover/popover.component';
import {
    ClauseBundleService,
    IClauseBundleWithClauses,
} from '../../../../services/clause-bundle.service';
import {
    CLAUSE_SOURCE,
    CLAUSE_STATUS,
    ClauseLibraryService,
    ClauseSortBy,
    ClauseSortOrder,
    IClause,
    IClauseFilters,
} from '../../../../services/clause-library.service';
import { UserService } from '../../../../services/user.service';
import { IUser } from '../../../../../@core/models/model';
import { AvatarModule } from 'primeng/avatar';
import { CheckboxModule } from 'primeng/checkbox';
import { SelectConfig } from '../../../../../@core/components/ui/select';
import { MultiSelectComponent } from '../../../../../@core/components/ui/multi-select';
import { DatepickerComponent } from '../../../../../@core/components/ui/datepicker';
import { TextCasePipe } from '../../../../../@shared/pipes/textCase.pipe';
import { MenuComponent, CustomMenuItem } from '../../../../../@core/components/ui/menu/menu.component';
import { CLAUSE_LIBRARY } from 'src/app/common/svgs';
import { IconSourceEnum } from 'src/app/@core/components/ui/icon/icon.component';
import { BlobLoaderComponent } from 'src/app/@shared/components/blob-loader/blob-loader.component';
import { UserNameDisplayComponent } from 'src/app/@shared/components/user-name-display';
import { SOCKET_EVENTS, SocketService } from '../../../../../@core/services/socket.service';
import { THREAD_STREAMING_STATUS } from 'src/app/features/general-agent/models/model';

@Component({
    selector: 'app-clause-bundle',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        DatePipe,
        DialogComponent,
        DrawerModule,
        ConfirmDialogModule,
        SkeletonModule,
        TooltipModule,
        AvatarModule,
        CheckboxModule,
        MultiSelectComponent,
        ButtonComponent,
        SearchBarComponent,
        DataTableComponent,
        PopoverComponent,
        DeleteConfirmationComponent,
        UserAvatarPipe,
        DatepickerComponent,
        TextCasePipe,
        MenuComponent,
        BlobLoaderComponent,
        UserNameDisplayComponent
    ],
    providers: [ConfirmationService],
    templateUrl: './clause-bundle.component.html',
    styleUrl: './clause-bundle.component.scss',
})
export class ClauseBundleComponent implements OnDestroy {
    @ViewChild('clauseNameTemplate') clauseNameTemplate!: TemplateRef<unknown>;
    @ViewChild('sourceTemplate') sourceTemplate!: TemplateRef<unknown>;
    @ViewChild('createdByTemplate') createdByTemplate!: TemplateRef<unknown>;
    @ViewChild('createdDateTemplate') createdDateTemplate!: TemplateRef<unknown>;
    @ViewChild('usageCountTemplate') usageCountTemplate!: TemplateRef<unknown>;
    @ViewChild('actionsTemplate') actionsTemplate!: TemplateRef<unknown>;
    @ViewChild('sortOverlay') sortOverlay!: PopoverComponent;

    bundles: IClauseBundleWithClauses[] = [];
    bundlesLoading = false;
    selectedBundle: IClauseBundleWithClauses | null = null;
    clauses: IClause[] = [];
    clausesTotal = 0;
    clausesLoading = false;
    clauseSkip = 0;
    clauseLimit = 20;
    searchValue = '';
    currentOpenPopover: unknown = null;

    // Sort state
    ClauseSortBy = ClauseSortBy;
    clauseSortBy: ClauseSortBy = ClauseSortBy.CreatedAt;
    clauseSortOrder: ClauseSortOrder = -1;

    addBundleDialogVisible = false;
    editBundleDialogVisible = false;
    bundleForm = new FormGroup({
        name: new FormControl('', [Validators.required]),
    });
    get bundleFormControls() {
        return { name: this.bundleForm.controls['name'] };
    }
    editedBundleName = '';

    addClauseDialogVisible = false;
    editClauseDialogVisible = false;
    editingClauseId: string | null = null;
    clauseForm = new FormGroup({
        name: new FormControl('', [Validators.required]),
        text: new FormControl('', [Validators.required]),
        source: new FormControl<string>('MANUAL', [Validators.required]),
    });
    get clauseFormControls() {
        return {
            name: this.clauseForm.controls['name'],
            text: this.clauseForm.controls['text'],
            source: this.clauseForm.controls['source'],
        };
    }
    sourceOptions: { label: string; value: CLAUSE_SOURCE }[] = [
        { label: 'Manual', value: CLAUSE_SOURCE.MANUAL },
        { label: 'Excel', value: CLAUSE_SOURCE.EXCEL },
        { label: 'Extracted', value: CLAUSE_SOURCE.EXTRACTED },
    ];
    sourceSelectConfig: SelectConfig = {
        placeholder: 'Select source',
        styleClass: 'w-full border border-gray-300 rounded-base',
    };

    showDeleteBundleDialog = false;
    showDeleteClauseDialog = false;
    deleteClauseIds: string[] = [];

    // Bulk edit dialog
    showBulkEditDialog = false;
    editingClauses: IClause[] = [];
    originalClauses: IClause[] = [];
    currentEditIndex = 0;
    savedClauses: Set<number> = new Set();
    totalEditingCount = 0;
    isBulkEditPreviousDisabled = true;
    isBulkEditNextDisabled = true;
    isBulkEditSaveDisabled = true;
    isBulkEditSaving = false;
    bulkEditGenerateLoading = false;
    bulkEditRephraseLoading = false;
    /** threadId of the in-flight generate/rephrase request for the currently active bulk-edit row. */
    pendingBulkClauseTextThreadId: string | null = null;
    pendingBulkClauseTextEditIndex: number | null = null;
    showPromptForClauseDialog = false;
    promptForClauseText = '';
    bulkEditClauseTextExpanded = false;
    dots$ = interval(800).pipe(map((i: number) => '.'.repeat(i % 4)));
    ICON_SOURCE = IconSourceEnum;
    CLAUSE_LIBRARY_ICONS = CLAUSE_LIBRARY;

    selectedClauses: IClause[] = [];
    filterDialog = false;
    filterState: IClauseFilters = {};
    bundleFilterOptions: { id: string; name: string }[] = [];

    // Filter form state (same as clause-library)
    sourceFilterOptions: { name: string; value: CLAUSE_SOURCE }[] = [
        { name: 'Manual entry', value: CLAUSE_SOURCE.MANUAL },
        { name: 'Excel', value: CLAUSE_SOURCE.EXCEL },
        { name: 'Extracted', value: CLAUSE_SOURCE.EXTRACTED },
    ];
    selectedSources: CLAUSE_SOURCE[] = [];
    userOptions: { label: string; value: string }[] = [];
    selectedCreators: string[] = [];
    selectedDateRange: Date[] | null = null;
    selectedDateChip: 'today' | 'last7days' | 'last30days' | null = null;
    minUsageCount: number | null = null;
    maxUsageCount: number | null = null;

    tableConfig: TableConfig = {
        selectable: true,
        paginator: true,
        rows: 20,
        rowsPerPageOptions: [20, 30, 40],
        scrollable: true,
        scrollHeight: "calc(100vh - 20rem)",
        loading: true,
        emptyMessage: 'No clauses in this bundle',
    };
    tableColumns: TableColumn[] = [
        { field: 'name', header: 'Clause name', type: 'custom', template: 'clauseNameTemplate' },
        { field: 'source', header: 'Source', type: 'custom', template: 'sourceTemplate' },
        { field: 'createdBy', header: 'Created by', type: 'custom', template: 'createdByTemplate' },
        { field: 'createdAt', header: 'Created date', type: 'custom', template: 'createdDateTemplate' },
        { field: 'usageCount', header: 'Usage count', type: 'custom', template: 'usageCountTemplate' },
        { field: 'actions', header: 'Actions', type: 'custom', template: 'actionsTemplate' },
    ];

    private clauseTextResultSubscription?: Subscription;

    constructor(
        private clauseBundleService: ClauseBundleService,
        private clauseLibraryService: ClauseLibraryService,
        private userService: UserService,
        private toast: ToastService,
        private cdr: ChangeDetectorRef,
        private confirmationService: ConfirmationService,
        private socketService: SocketService
    ) { }

    ngOnDestroy() {
        this.clauseTextResultSubscription?.unsubscribe();
    }

    /**
     * Routes async generate/rephrase clause-text results (streamed from the Python
     * ClauseTextAgent via RabbitMQ -> Node -> socket) back to the active bulk-edit row
     * that fired the matching threadId.
     */
    private setupClauseTextResultSocket() {
        this.clauseTextResultSubscription = this.socketService
            .on<{ status: string; content: string; threadId: string }>(SOCKET_EVENTS.CLAUSE_TEXT_RESULT)
            .subscribe((res) => {
                const payload = res[0];
                if (!payload) return;
                const { status, content, threadId } = payload;
                const isDone = status === THREAD_STREAMING_STATUS.COMPLETED;
                const isError = status === THREAD_STREAMING_STATUS.ERROR;
                if (!isDone && !isError) return;
                if (threadId !== this.pendingBulkClauseTextThreadId) return;

                this.bulkEditGenerateLoading = false;
                this.bulkEditRephraseLoading = false;
                const editIndex = this.pendingBulkClauseTextEditIndex;
                this.pendingBulkClauseTextThreadId = null;
                this.pendingBulkClauseTextEditIndex = null;
                if (isDone && editIndex != null && this.editingClauses[editIndex]) {
                    this.editingClauses[editIndex] = { ...this.editingClauses[editIndex], text: content };
                    this.saveClauseInBulkEdit(editIndex);
                    this.toast.success('Clause text updated');
                } else if (isError) {
                    this.toast.error('Something went wrong, please try again');
                }
            });
    }

    get templatesMap(): { [key: string]: TemplateRef<unknown> } {
        return {
            clauseNameTemplate: this.clauseNameTemplate,
            sourceTemplate: this.sourceTemplate,
            createdByTemplate: this.createdByTemplate,
            createdDateTemplate: this.createdDateTemplate,
            usageCountTemplate: this.usageCountTemplate,
            actionsTemplate: this.actionsTemplate,
        };
    }

    ngOnInit() {
        this.loadBundles();
        this.loadBundleFilterOptions();
        this.getUsers();
        this.setupClauseTextResultSocket();
    }

    getUsers() {
        this.userService.getWorkspaceUsers({ includeAdmins: true }).subscribe((res) => {
            if (res?.success && res.data) {
                this.userOptions = (res.data as IUser[]).map((user) => ({
                    label: user.fullName || `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || user.email,
                    value: user._id,
                }));
                this.cdr.detectChanges();
            }
        });
    }

    loadBundleFilterOptions() {
        this.clauseBundleService.getBundleFilterOptions().subscribe((res) => {
            if (res?.success && res.data?.bundles) {
                this.bundleFilterOptions = res.data.bundles;
                this.cdr.detectChanges();
            }
        });
    }

    loadBundles(selectBundleId?: string) {
        this.bundlesLoading = true;
        this.clauseBundleService.getBundles({}, true).subscribe((res) => {
            if (res?.success && res.data) {
                this.bundles = (res.data.bundles || []) as IClauseBundleWithClauses[];
                this.bundlesLoading = false;
                if (selectBundleId) {
                    const found = this.bundles.find((b) => b._id === selectBundleId);
                    this.selectedBundle = found ?? this.bundles[0] ?? null;
                    this.loadClauses();
                } else if (this.bundles.length > 0 && !this.selectedBundle) {
                    this.selectBundle(this.bundles[0]);
                } else if (this.selectedBundle) {
                    const found = this.bundles.find((b) => b._id === this.selectedBundle!._id);
                    if (found) this.selectedBundle = found;
                    this.loadClauses();
                }
                this.cdr.detectChanges();
            } else {
                this.bundlesLoading = false;
                this.cdr.detectChanges();
            }
        });
    }

    selectBundle(bundle: IClauseBundleWithClauses) {
        this.selectedBundle = bundle;
        this.clauseSkip = 0;
        this.loadClauses();
        this.cdr.detectChanges();
    }

    loadClauses() {
        if (!this.selectedBundle?._id) {
            this.clauses = [];
            this.tableConfig = { ...this.tableConfig, loading: false };
            return;
        }
        this.clausesLoading = true;
        this.tableConfig = { ...this.tableConfig, loading: true };
        const filters: IClauseFilters = {
            ...this.filterState,
            clauseBundle: [this.selectedBundle._id],
            searchValue: this.searchValue.trim() || undefined,
            status: CLAUSE_STATUS.APPROVED,
            skip: this.clauseSkip,
            limit: this.clauseLimit,
            sortBy: this.clauseSortBy,
            sortOrder: this.clauseSortOrder,
        };
        this.clauseLibraryService.getClauses(filters).subscribe((res) => {
            if (res?.success && res.data) {
                this.clauses = res.data.clauses || [];
                this.clausesTotal = res.data.total;
                this.clausesTotal = res.data.total ?? 0;
                this.clausesLoading = false;
                this.tableConfig = { ...this.tableConfig, loading: false };
                this.cdr.detectChanges();
            } else {
                this.clausesLoading = false;
                this.tableConfig = { ...this.tableConfig, loading: false };
                this.cdr.detectChanges();
            }
        });
    }

    onSearchChange() {
        this.clauseSkip = 0;
        this.loadClauses();
    }

    onPageChange(event: any) {
        this.clauseSkip = event.first;
        this.clauseLimit = event.rows;
        this.loadClauses();
    }

    setClauseSort(sortBy: ClauseSortBy) {
        this.clauseSortBy = sortBy;
        this.clauseSortOrder = sortBy === ClauseSortBy.Name ? 1 : -1;
        this.clauses = [];
        this.sortOverlay?.hide();
        this.loadClauses();
    }

    setSortOrder(order: ClauseSortOrder) {
        this.clauseSortOrder = order;
        this.clauses = [];
        this.sortOverlay?.hide();
        this.loadClauses();
    }

    handlePopoverOpen(popover: unknown) {
        if (this.currentOpenPopover && this.currentOpenPopover !== popover) {
            (this.currentOpenPopover as { hide(): void }).hide?.();
        }
        this.currentOpenPopover = popover;
    }

    openAddBundleDialog() {
        this.bundleForm.reset({ name: '' });
        this.addBundleDialogVisible = true;
    }

    openEditBundleDialog() {
        if (!this.selectedBundle) return;
        this.editedBundleName = this.selectedBundle.name;
        this.editBundleDialogVisible = true;
    }

    createBundle() {
        const name = this.bundleForm.value.name?.trim();
        if (!name) return;
        this.clauseBundleService.createBundle(name).subscribe((res) => {
            if (res?.success && res.data) {
                this.toast.success('Clause bundle created successfully');
                this.addBundleDialogVisible = false;
                const newId = (res.data as { _id?: string })._id;
                this.loadBundles(newId);
                this.cdr.detectChanges();
            } else {
                this.toast.error('Failed to create clause bundle');
            }
        });
    }

    updateBundle() {
        if (!this.selectedBundle?._id) return;
        const name = this.editedBundleName?.trim();
        if (!name) return;
        this.clauseBundleService.updateBundle(this.selectedBundle._id, { name }).subscribe((res) => {
            if (res?.success) {
                this.toast.success('Clause bundle updated successfully');
                this.editBundleDialogVisible = false;
                this.loadBundles();
                this.cdr.detectChanges();
            } else {
                this.toast.error('Failed to update clause bundle');
            }
        });
    }

    openDeleteBundleDialog() {
        this.showDeleteBundleDialog = true;
    }

    getBundleMenuOptions(bundle: IClauseBundleWithClauses): CustomMenuItem[] {
        return [
            { label: 'Edit', icon: 'pi pi-pencil', value: 'edit', callback: () => { this.selectBundle(bundle); this.openEditBundleDialog(); } },
            { label: 'Delete', icon: 'pi pi-trash', value: 'delete', class: 'text-danger', callback: () => { this.selectBundle(bundle); this.openDeleteBundleDialog(); } }
        ];
    }

    deleteBundle() {
        if (!this.selectedBundle?._id) return;
        this.clauseBundleService.deleteBundle(this.selectedBundle._id).subscribe((res) => {
            if (res?.success) {
                this.toast.success('Clause bundle deleted successfully');
                this.showDeleteBundleDialog = false;
                this.selectedBundle = this.bundles[0] === this.selectedBundle ? null : this.selectedBundle;
                this.loadBundles();
                this.cdr.detectChanges();
            } else {
                this.toast.error('Failed to delete clause bundle');
            }
        });
    }

    openAddClauseDialog() {
        this.clauseForm.reset({
            name: '',
            text: '',
            source: CLAUSE_SOURCE.MANUAL,
        });
        this.editingClauseId = null;
        this.addClauseDialogVisible = true;
    }

    openEditClauseDialog(clause: IClause) {
        this.editingClauseId = clause._id;
        this.clauseForm.patchValue({
            name: clause.name,
            text: clause.text ?? '',
            source: clause.source ?? CLAUSE_SOURCE.MANUAL,
        });
        this.editClauseDialogVisible = true;
    }

    openBulkEdit() {
        if (this.selectedClauses.length === 0) {
            this.toast.error('Please select at least one clause to edit');
            return;
        }
        if (this.selectedClauses.length === 1) {
            this.openEditClauseDialog(this.selectedClauses[0]);
            return;
        }
        this.editingClauses = this.selectedClauses.map((c) => ({ ...c }));
        this.originalClauses = this.selectedClauses.map((c) => ({ ...c }));
        this.currentEditIndex = 0;
        this.showBulkEditDialog = true;
        this.totalEditingCount = this.editingClauses.length;
        this.isBulkEditPreviousDisabled = true;
        this.isBulkEditNextDisabled = this.editingClauses.length <= 1;
        this.savedClauses.clear();
        this.isBulkEditSaveDisabled = true;
        this.bulkEditGenerateLoading = false;
        this.bulkEditRephraseLoading = false;
        this.bulkEditClauseTextExpanded = false;
    }

    openPromptForBulkEditClauseDialog() {
        const clause = this.editingClauses[this.currentEditIndex];
        if (!clause?.name?.trim()) {
            this.toast.error('Please enter clause name first');
            return;
        }
        this.promptForClauseText = '';
        this.showPromptForClauseDialog = true;
    }

    closePromptForClauseDialog() {
        this.showPromptForClauseDialog = false;
        this.promptForClauseText = '';
    }

    submitPromptForBulkEditClause() {
        const clause = this.editingClauses[this.currentEditIndex];
        const name = (clause?.name ?? '').trim();
        const prompt = (this.promptForClauseText ?? '').trim();
        if (!name) {
            this.toast.error('Please enter clause name first');
            return;
        }
        this.closePromptForClauseDialog();
        const editIndex = this.currentEditIndex;
        this.bulkEditGenerateLoading = true;
        this.clauseLibraryService.generateClauseText(name, prompt).subscribe({
            next: (res: any) => {
                if (res?.success && res?.data?.threadId) {
                    this.pendingBulkClauseTextThreadId = res.data.threadId;
                    this.pendingBulkClauseTextEditIndex = editIndex;
                } else {
                    this.bulkEditGenerateLoading = false;
                    this.toast.error(res?.message || 'Failed to start clause text generation');
                }
            },
            error: () => {
                this.bulkEditGenerateLoading = false;
                this.toast.error('Failed to start clause text generation');
            }
        });
    }

    generateClauseTextForBulkEdit() {
        this.openPromptForBulkEditClauseDialog();
    }

    toggleBulkEditClauseTextExpanded() {
        this.bulkEditClauseTextExpanded = !this.bulkEditClauseTextExpanded;
    }

    rephraseClauseForBulkEdit() {
        const clause = this.editingClauses[this.currentEditIndex];
        const name = (clause?.name ?? '').trim();
        const text = (clause?.text ?? '').trim();
        if (!text) {
            this.toast.error('Clause text is empty');
            return;
        }
        const editIndex = this.currentEditIndex;
        this.bulkEditRephraseLoading = true;
        this.clauseLibraryService.rephraseClause(name, text).subscribe({
            next: (res) => {
                if (res?.success && res?.data?.threadId) {
                    this.pendingBulkClauseTextThreadId = res.data.threadId;
                    this.pendingBulkClauseTextEditIndex = editIndex;
                } else {
                    this.bulkEditRephraseLoading = false;
                    this.toast.error(res?.message || 'Failed to start clause text rephrase');
                }
            },
            error: () => {
                this.bulkEditRephraseLoading = false;
                this.toast.error('Failed to start clause text rephrase');
            }
        });
    }

    onBulkEditDialogHide() {
        if (this.bulkEditGenerateLoading || this.bulkEditRephraseLoading) {
            this.confirmationService.confirm({
                message: 'The clause is currently being generated. If you exit now, it will not be updated. Are you sure you want to cancel?',
                header: 'Cancel generation?',
                acceptLabel: 'Yes',
                rejectLabel: 'No',
                accept: () => {
                    this.pendingBulkClauseTextThreadId = null;
                    this.pendingBulkClauseTextEditIndex = null;
                    this.showBulkEditDialog = false;
                    this.bulkEditGenerateLoading = false;
                    this.bulkEditRephraseLoading = false;
                    this.onCancelBulkEdit();
                },
                reject: () => {
                    this.showBulkEditDialog = true;
                }
            });
            return;
        }
        this.showBulkEditDialog = false;
        this.onCancelBulkEdit();
    }

    selectClauseForEdit(index: number) {
        if ((this.bulkEditGenerateLoading || this.bulkEditRephraseLoading) && index !== this.currentEditIndex) {
            this.confirmationService.confirm({
                message: 'The clause is currently being generated. If you switch now, the result will not be applied. Are you sure you want to cancel?',
                header: 'Cancel generation?',
                acceptLabel: 'Yes',
                rejectLabel: 'No',
                accept: () => {
                    this.pendingBulkClauseTextThreadId = null;
                    this.pendingBulkClauseTextEditIndex = null;
                    this.bulkEditGenerateLoading = false;
                    this.bulkEditRephraseLoading = false;
                    this.currentEditIndex = index;
                    this.isBulkEditPreviousDisabled = index === 0;
                    this.isBulkEditNextDisabled = index === this.editingClauses.length - 1;
                    this.cdr.detectChanges();
                },
                reject: () => { }
            });
            return;
        }
        this.currentEditIndex = index;
        this.isBulkEditPreviousDisabled = index === 0;
        this.isBulkEditNextDisabled = index === this.editingClauses.length - 1;
    }

    navigateBulkEditPrevious() {
        if (this.currentEditIndex > 0) {
            this.currentEditIndex--;
        }
        this.isBulkEditPreviousDisabled = this.currentEditIndex === 0;
        this.isBulkEditNextDisabled = this.currentEditIndex === this.editingClauses.length - 1;
    }

    navigateBulkEditNext() {
        if (this.currentEditIndex < this.editingClauses.length - 1) {
            this.currentEditIndex++;
        }
        this.isBulkEditPreviousDisabled = this.currentEditIndex === 0;
        this.isBulkEditNextDisabled = this.currentEditIndex === this.editingClauses.length - 1;
    }

    saveClauseInBulkEdit(index: number) {
        const orig = this.originalClauses[index];
        const edit = this.editingClauses[index];
        const changed = (orig.name !== edit.name) || (orig.text !== edit.text);
        if (changed) {
            this.savedClauses.add(index);
        } else {
            this.savedClauses.delete(index);
        }
        this.isBulkEditSaveDisabled = this.savedClauses.size === 0;
    }

    onCancelBulkEdit() {
        this.showBulkEditDialog = false;
        this.editingClauses = [];
        this.originalClauses = [];
        this.currentEditIndex = 0;
        this.savedClauses.clear();
        this.totalEditingCount = 0;
        this.isBulkEditPreviousDisabled = true;
        this.isBulkEditNextDisabled = true;
        this.isBulkEditSaveDisabled = true;
        this.selectedClauses = [];
        this.bulkEditGenerateLoading = false;
        this.bulkEditRephraseLoading = false;
        this.bulkEditClauseTextExpanded = false;
        this.cdr.detectChanges();
    }

    onSaveBulkEdit() {
        const modifiedClauses = Array.from(this.savedClauses).map((index) => {
            const clause = this.editingClauses[index];
            return { _id: clause._id, name: clause.name ?? '', text: clause.text ?? '' };
        });
        if (modifiedClauses.length === 0) {
            this.toast.error('No changes to save');
            return;
        }
        this.isBulkEditSaving = true;
        this.clauseLibraryService.bulkUpdateClauses(modifiedClauses).subscribe({
            next: (res) => {
                this.isBulkEditSaving = false;
                if (res?.success) {
                    this.toast.success(`${modifiedClauses.length} clause(s) updated successfully`);
                    this.showBulkEditDialog = false;
                    this.editingClauses = [];
                    this.originalClauses = [];
                    this.savedClauses.clear();
                    this.selectedClauses = [];
                    this.loadClauses();
                    this.loadBundles();
                    this.cdr.detectChanges();
                } else {
                    this.toast.error('Failed to update clause(s)');
                }
            },
            error: () => {
                this.isBulkEditSaving = false;
                this.toast.error('Failed to update clause(s)');
            },
        });
    }

    saveClause() {
        if (this.clauseForm.invalid) return;
        const { name, text } = this.clauseForm.value;
        if (!name?.trim() || !text?.trim()) return;
        const clauseBundles = this.selectedBundle?._id ? [this.selectedBundle._id] : [];

        if (this.editingClauseId) {
            this.clauseLibraryService
                .updateClause(this.editingClauseId, {
                    name: name.trim(),
                    text: text.trim(),
                    source: CLAUSE_SOURCE.MANUAL,
                    clauseBundles,
                })
                .subscribe((res) => {
                    if (res?.success) {
                        this.toast.success('Clause updated successfully');
                        this.editClauseDialogVisible = false;
                        this.loadClauses();
                        this.cdr.detectChanges();
                    } else {
                        this.toast.error('Failed to update clause');
                    }
                });
        } else {
            this.clauseLibraryService
                .createClause({
                    name: name.trim(),
                    text: text.trim(),
                    source: CLAUSE_SOURCE.MANUAL,
                    clauseBundles,
                })
                .subscribe((res) => {
                    if (res?.success) {
                        this.toast.success('Clause created successfully');
                        this.addClauseDialogVisible = false;
                        this.loadClauses();
                        this.loadBundles();
                        this.cdr.detectChanges();
                    } else {
                        this.toast.error('Failed to create clause');
                    }
                });
        }
    }

    onClauseSelectionChange(selected: IClause[] | unknown) {
        this.selectedClauses = Array.isArray(selected) ? selected : [];
    }

    openDeleteClauseDialog(clause?: IClause) {
        if (clause) {
            this.deleteClauseIds = [clause._id];
        } else {
            this.deleteClauseIds = this.selectedClauses.map((c) => c._id);
        }
        this.showDeleteClauseDialog = true;
    }

    getClauseMenuOptions(clause: IClause): CustomMenuItem[] {
        return [
            { label: 'Edit', icon: 'pi pi-pencil', value: 'edit', callback: () => this.openEditClauseDialog(clause) },
            { label: 'Delete', icon: 'pi pi-trash', value: 'delete', class: 'text-danger', callback: () => this.openDeleteClauseDialog(clause) }
        ];
    }

    getSourceLabel(source: CLAUSE_SOURCE): string {
        switch (source) {
            case CLAUSE_SOURCE.MANUAL:
                return 'Manual entry';
            case CLAUSE_SOURCE.EXCEL:
                return 'Excel';
            case CLAUSE_SOURCE.EXTRACTED:
                return 'Extracted';
            default:
                return source ?? '';
        }
    }

    deleteClauses() {
        if (this.deleteClauseIds.length === 0) return;
        this.clauseLibraryService.bulkDeleteClauses(this.deleteClauseIds).subscribe((res) => {
            if (res?.success) {
                this.toast.success('Clause(s) deleted successfully');
                this.showDeleteClauseDialog = false;
                this.selectedClauses = [];
                this.loadClauses();
                this.loadBundles();
                this.cdr.detectChanges();
            } else {
                this.toast.error('Failed to delete clause(s)');
            }
        });
    }

    downloadSelectedClauses() {
        if (this.selectedClauses.length === 0) return;
        const ids = this.selectedClauses.map((c) => c._id);
        this.clauseLibraryService.bulkDownloadClauses(ids).subscribe({
            next: (res) => {
                if (res.success && res.data) {
                    const blob = new Blob([new Uint8Array(res.data.data)], {
                        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=utf-8',
                    });
                    saveAs(blob, 'clauses.xlsx');
                    this.toast.success(`${this.selectedClauses.length} clause(s) downloaded successfully`);
                } else {
                    this.toast.error('Download failed');
                }
            },
            error: () => this.toast.error('Download failed'),
        });
    }

    delinkSelectedFromBundle() {
        if (this.selectedClauses.length === 0 || !this.selectedBundle?._id) return;
        const updateCalls = this.selectedClauses.map((c) => {
            const currentBundles = (c.clauseBundles || []).map((b: string | { _id: string }) =>
                typeof b === 'string' ? b : b._id
            );
            const newBundles = currentBundles.filter((id) => id !== this.selectedBundle!._id);
            return this.clauseLibraryService.updateClause(c._id, { clauseBundles: newBundles });
        });
        forkJoin(updateCalls).subscribe({
            next: () => {
                this.toast.success('Clause(s) delinked from bundle');
                this.selectedClauses = [];
                this.loadClauses();
                this.loadBundles();
                this.cdr.detectChanges();
            },
            error: () => {
                this.toast.error('Failed to delink some clause(s)');
                this.loadClauses();
                this.loadBundles();
                this.cdr.detectChanges();
            },
        });
    }

    openFilterDialog() {
        this.syncFilterFormFromState();
        this.filterDialog = true;
    }

    syncFilterFormFromState() {
        this.selectedSources = this.filterState.source ?? [];
        this.selectedCreators = this.filterState.createdBy ?? [];
        this.minUsageCount = this.filterState.minUsageCount ?? null;
        this.maxUsageCount = this.filterState.maxUsageCount ?? null;
        if (this.filterState.createdAt?.startDate && this.filterState.createdAt?.endDate) {
            this.selectedDateRange = [
                new Date(this.filterState.createdAt.startDate),
                new Date(this.filterState.createdAt.endDate),
            ];
        } else {
            this.selectedDateRange = null;
            this.selectedDateChip = null;
        }
    }

    applyFilter() {
        this.filterState = {
            source: this.selectedSources.length > 0 ? this.selectedSources : undefined,
            createdBy: this.selectedCreators.length > 0 ? this.selectedCreators : undefined,
            createdAt:
                this.selectedDateRange?.length === 2
                    ? {
                        startDate: this.selectedDateRange[0],
                        endDate: this.selectedDateRange[1],
                    }
                    : undefined,
            minUsageCount: this.minUsageCount ?? undefined,
            maxUsageCount: this.maxUsageCount ?? undefined,
        };
        this.filterDialog = false;
        this.clauseSkip = 0;
        this.loadClauses();
        this.cdr.detectChanges();
    }

    clearFilter() {
        this.selectedSources = [];
        this.selectedCreators = [];
        this.selectedDateRange = null;
        this.selectedDateChip = null;
        this.minUsageCount = null;
        this.maxUsageCount = null;
        this.filterState = {};
        this.filterDialog = false;
        this.clauseSkip = 0;
        this.loadClauses();
        this.cdr.detectChanges();
    }

    selectDateRange(range: 'today' | 'last7days' | 'last30days') {
        const today = new Date();
        today.setHours(23, 59, 59, 999);
        let startDate = new Date();
        switch (range) {
            case 'today':
                startDate.setHours(0, 0, 0, 0);
                this.selectedDateChip = 'today';
                break;
            case 'last7days':
                startDate.setDate(today.getDate() - 7);
                startDate.setHours(0, 0, 0, 0);
                this.selectedDateChip = 'last7days';
                break;
            case 'last30days':
                startDate.setDate(today.getDate() - 30);
                startDate.setHours(0, 0, 0, 0);
                this.selectedDateChip = 'last30days';
                break;
        }
        this.selectedDateRange = [startDate, today];
    }

    getDateChipClass(range: 'today' | 'last7days' | 'last30days'): string {
        const isSelected = this.selectedDateChip === range;
        return isSelected
            ? 'bg-gray-700 text-white hover:bg-gray-800'
            : 'bg-gray-200 text-gray-700 hover:bg-gray-300';
    }

    onDateRangeChange() {
        this.selectedDateChip = null;
    }

    get currentDate(): Date {
        return new Date();
    }

    getBundleClauseCount(bundle: IClauseBundleWithClauses): number {
        return Array.isArray(bundle.clauses) ? bundle.clauses.length : 0;
    }
}
